import {PubSub} from '@mamdev/cad.static-next-lib';
import viewport from './viewport';

/**
 * Service to handle scroll related stuff.
 * For now this is just used to track the depth of scrolling within a specified Node (currently used for article & hp)
 */
class Scrolling {
  content25Reached_ = false;
  content50Reached_ = false;
  content75Reached_ = false;
  content200pxReached_ = false;
  content400pxReached_ = false;
  contentInteracted_ = false;
  endReached_ = false;
  endCord_ = 1;

  /**
   * Fires a tracking pixel depending on the scroll depth
   * @param {number} startCord starting coordinates where counting starts
   * @param {HTMLElement} endNode Node which basically means 100% reached
   */
  handleScrollProgress(startCord: number, endNode: HTMLElement | undefined) {
    if (!endNode) return false;
    if (this.endReached_) return false;

    // Hint: this value is calculated from an initially retrieved DOM position, which may change later by lazy-loaded elements
    const readProgress = window.scrollY / this.endCord_;

    const emptyClicks = {
      click1: 'undefined',
      click2: 'undefined',
      click3: 'undefined',
      click4: 'undefined'
    };

    // [CADNPCA-9482] fired once during scroll
    if (!this.contentInteracted_) {
      PubSub.publish('content:articleReadProgress', Object.assign(emptyClicks, {
        clickname: 'content_0_viewed'
      }));
      this.contentInteracted_ = true;
    }

    // [CADNPCA-9482] fired once past 200 pixel
    if (!this.content200pxReached_ && window.scrollY >= 200) {
      PubSub.publish('content:articleReadProgress', Object.assign(emptyClicks, {
        clickname: 'content_200px_viewed'
      }));
      this.content200pxReached_ = true;
    }

    // [CADNPCA-9482] fired for once past 400 pixel
    if (!this.content400pxReached_ && window.scrollY >= 400) {
      PubSub.publish('content:articleReadProgress', Object.assign(emptyClicks, {
        clickname: 'content_400px_viewed'
      }));
      this.content400pxReached_ = true;
    }

    if (!this.content25Reached_ && readProgress >= 0.25) {
      PubSub.publish('content:articleReadProgress', Object.assign(emptyClicks, {
        clickname: 'content_25_viewed'
      }));
      this.content25Reached_ = true;
    }

    if (!this.content50Reached_ && readProgress >= 0.5) {
      this.recalcScrollEnd(endNode, startCord);
      PubSub.publish('content:articleReadProgress', Object.assign(emptyClicks, {
        clickname: 'content_50_viewed'
      }));
      this.content50Reached_ = true;
    }

    if (!this.content75Reached_ && readProgress >= 0.75) {
      PubSub.publish('content:articleReadProgress', Object.assign(emptyClicks, {
        clickname: 'content_75_viewed'
      }));
      this.content75Reached_ = true;
    }

    if (!this.endReached_ && readProgress >= 1) {
      PubSub.publish('content:articleReadProgress', Object.assign(emptyClicks, {
        clickname: 'content_end_viewed'
      }));
      this.endReached_ = true;
    }

    // So far pixel scroll-depth as CCR tracking is only used for normal articles
    // Video and slideshow pages will have different contentType value (from `ui.recom`)
    PubSub.publish('content:recoCCR', {progress: readProgress, contentType: 'Article'});
  }

  /**
   * recalculate ScrollEnd
   * @param {HTMLElement} endNode Node which basically means 100% reached
   * @param {number} startCord
   */
  recalcScrollEnd(endNode: HTMLElement | undefined, startCord: number) {
    // Hint: the position of "endNode" could change later during scrolling if there are lazy-loaded elements
    if (endNode) this.endCord_ = (viewport.getElementYOffset(endNode) - startCord);
  }

  /**
   * Intersection Observer, calls the callback function if the element has been reached
   * disconnects the observer after it successfully done the job
   */
  observeElement(callback: () => void): IntersectionObserver {
    const elementObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;

        /* visual test if the element was in VP
        const test = entry.target as HTMLElement;
        test.style.opacity = '0.5';
        */
        callback();

        // after element was observed, disconnect the observer to avoid tracking it multiple times
        elementObserver.disconnect();
      });
    }, {
      root: null,
      rootMargin: '0px',
      threshold: 0
    });

    return elementObserver;
  }
}

export default new Scrolling();
